import { triggerBlobDownload } from '@/utils/download'
import { Badge, Button, Card, Group, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FaDownload } from 'react-icons/fa'
import { TbAlertTriangle } from 'react-icons/tb'

export type DownloadedLog = { blob: Blob; fileName: string }

/**
 * @param fileName name shown before the download starts; the downloaded file may announce a more specific one
 * @param fetchLog resolves to null when the vehicle has no log file to offer
 */
const LogDownloadCard = ({
  label,
  fileName,
  sizeMb,
  fetchLog,
}: {
  label: string
  fileName: string
  sizeMb: number
  fetchLog: () => Promise<DownloadedLog | null>
}) => {
  const { t } = useTranslation(['diagnostics', 'common'])

  const { mutate: download, isPending } = useMutation({
    mutationFn: fetchLog,
    onSuccess: log => {
      if (!log) {
        notifications.show({ message: t('noLogFile'), color: 'yellow' })
        return
      }
      triggerBlobDownload(log.blob, log.fileName)
      notifications.show({ message: t('downloadStarted', { fileName: log.fileName }), color: 'green' })
    },
    onError: () => notifications.show({ message: t('downloadFailed', { fileName }), color: 'red' }),
  })

  const confirmDownload = () =>
    modals.openConfirmModal({
      title: (
        <Group gap="xs" align="center">
          <TbAlertTriangle size={16} color="var(--mantine-color-orange-6)" />
          <Text fw={500}>{t('downloadWarning')}</Text>
        </Group>
      ),
      children: <Text size="sm">{t('confirmDownload', { fileName, size: sizeMb })}</Text>,
      labels: { confirm: t('download'), cancel: t('cancel', { ns: 'common' }) },
      confirmProps: { color: 'orange' },
      onConfirm: () => download(),
    })

  return (
    <Card shadow="sm" p="lg" radius="md" withBorder>
      <Group justify="space-between" align="center">
        <Group align="center">
          <Text fw={500}>{label}</Text>
          <Badge variant="light" color="orange">
            {t('downloadSizeHint', { size: sizeMb })}
          </Badge>
        </Group>
        <Button leftSection={<FaDownload size={12} />} onClick={confirmDownload} loading={isPending}>
          {t('download')}
        </Button>
      </Group>
    </Card>
  )
}

export default LogDownloadCard
